import {
  HUB_EXPERIENCE_SURVEY_RESPONSE,
  HUB_WELLNESS_SURVEY_RESPONSE,
  SurveyResponse,
  SurveyType,
  WellnessResponse,
} from './constants';

// Mirrors SURVEY_TYPE keys in OmcareRestServer (`survey_type` on API rows).
export type SurveyTypeKey =
  'HUB_OVERALL_EXPERIENCE' | 'HUB_DAILY_WELLNESS_CHECK';

// Score → label lookup per survey type. Mirrors SURVEY_RESPONSE_BY_TYPE in
// OmcareRestServer so the frontend labels a raw score exactly like the API.
export const SURVEY_RESPONSE_BY_TYPE: Record<
  SurveyType,
  Record<number, string>
> = {
  [SurveyType.HubOverallExperience]: HUB_EXPERIENCE_SURVEY_RESPONSE,
  [SurveyType.HubDailyWellnessCheck]: HUB_WELLNESS_SURVEY_RESPONSE,
};

// One entry of `survey_responses[]` on GET /owners/:id/overview: the latest
// response the customer gave for that survey type.
export type LatestSurveyResponse = {
  survey_type_id: number;
  survey_type?: SurveyTypeKey | null;
  title?: string;
  score: number;
  min_score?: number;
  max_score?: number;
  label?: string | null;
  received_at?: string;
};

type HasSurveyResponses = {
  survey_responses?: LatestSurveyResponse[] | null;
};

// Latest response of `surveyType` from an overview payload, if any.
export const getLatestSurveyResponse = (
  overview: HasSurveyResponses | null | undefined,
  surveyType: SurveyType,
): LatestSurveyResponse | undefined =>
  (overview?.survey_responses ?? []).find(
    (row) => Number(row?.survey_type_id) === surveyType,
  );

// Accept either a label ('Good') or a raw score (4 / '4') and return the label
// on that survey type's scale, so cards render correctly whichever shape the
// backend sends. Unknown strings pass through; empty input yields null.
export const surveyScoreToLabel = (
  surveyType: SurveyType,
  value: string | number | null | undefined,
): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const asNumber = Number(value);
  const labels = SURVEY_RESPONSE_BY_TYPE[surveyType];
  if (Number.isInteger(asNumber) && labels?.[asNumber]) {
    return labels[asNumber];
  }
  return String(value);
};

// Survey rows carry no DB id; type + timestamp is unique per customer.
export const surveyRowId = (row: {
  survey_type_id?: number | null;
  received_at?: string | null;
}): string => `${row.survey_type_id ?? 'na'}-${row.received_at ?? ''}`;

// Scale bounds per survey type, derived from the label maps so they can never
// drift from the labels the API uses (CSAT 1-4, daily wellness check 1-5).
export type SurveyScale = { min: number; max: number };

export const SURVEY_SCALE_BY_TYPE: Record<SurveyType, SurveyScale> =
  Object.fromEntries(
    Object.entries(SURVEY_RESPONSE_BY_TYPE).map(([type, labels]) => {
      const scores = Object.keys(labels).map(Number);
      return [
        Number(type),
        { min: Math.min(...scores), max: Math.max(...scores) },
      ];
    }),
  ) as Record<SurveyType, SurveyScale>;

// Responses the Overview dashboard flags for follow-up: the two lowest on each
// scale (PatientSurveyDetailPanel / WellnessDetailPanel list the patients whose
// latest answer is one of these). The location Insights tab lists the patients
// whose lowest answer in its window falls in the same range, so every view
// agrees on who needs attention.
export const SURVEY_ATTENTION_RESPONSES: Record<SurveyType, readonly string[]> =
  {
    [SurveyType.HubOverallExperience]: [
      SurveyResponse.VeryUnhappy,
      SurveyResponse.SomewhatUnhappy,
    ],
    [SurveyType.HubDailyWellnessCheck]: [
      WellnessResponse.VeryBad,
      WellnessResponse.Bad,
    ],
  };

// Whether a label ('Bad') or raw score (2 / '2') on `surveyType`'s scale is
// one of SURVEY_ATTENTION_RESPONSES.
export const isSurveyAttentionResponse = (
  surveyType: SurveyType,
  value: string | number | null | undefined,
): boolean => {
  const label = surveyScoreToLabel(surveyType, value);
  return (
    label !== null && SURVEY_ATTENTION_RESPONSES[surveyType].includes(label)
  );
};

// 'Very bad or Bad' — the attention range of a survey type, for copy.
export const formatSurveyAttentionRange = (surveyType: SurveyType): string =>
  SURVEY_ATTENTION_RESPONSES[surveyType].join(' or ');

// Short display names used on the Overview cards and the Insights tab.
export const SURVEY_TYPE_DISPLAY_NAME: Record<SurveyType, string> = {
  [SurveyType.HubOverallExperience]: 'CSAT',
  [SurveyType.HubDailyWellnessCheck]: 'Wellness',
};

// Survey types in the order they are offered on the Insights tab.
export const INSIGHTS_SURVEY_TYPES: SurveyType[] = [
  SurveyType.HubOverallExperience,
  SurveyType.HubDailyWellnessCheck,
];
